import { inject } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { CanActivateFn, Router, UrlTree } from '@angular/router'
import { firstValueFrom } from 'rxjs'
import { environment } from '../../../environments/environment'
import { DEFAULT_SEASON } from '../../shared/constants/season.constants'
import { ApiResponse } from '../../shared/interfaces/api.interface'
import { UserRole } from '../../shared/interfaces/user.interface'
import { AuthService } from '../services/auth.service'

/**
 * One thing to show, no list to pick it from (CC, 10/10/2026): "if someone has access to only one
 * competition, bring him immediately" -- sign in, then the competition, not a grid of one card.
 *
 * - singleCompetitionGuard, on /home: a director holding exactly one competition goes to its
 *   Overview (every entity opens on Overview, CC 29/09/2026); a director with national teams
 *   goes to My teams instead (CC 23/10/2026).
 * - singleTeamGuard, on /my-teams: a coach with exactly one place goes to its Overview. A place is
 *   a team he holds, a national team or a competition granted to him: a national-team coach with
 *   Cyprus U15 and the Under 15 competition picks between them on /my-teams, rather than landing
 *   on the team with the competition hidden in the menu (role-by-role test, CC 22/10/2026).
 *
 * Anything unexpected -- the request fails, the account holds none or several -- shows the page
 * as before, so a hiccup never strands anyone. Both return a UrlTree rather than navigating, for
 * the reason in home-redirect.guard.ts: Back must not land on a page that sends you forward again.
 */

// The same placeholder the Home page leaves out of its national-team list (home.component.ts):
// counting it would make a director with one real competition look like they hold two.
const HIDDEN_COMPETITIONS = ['Cyprus FA']

interface LandingCompetition { id: string, name: string, scope?: string }
interface LandingTeam { id: string }
interface LandingReach { competitions?: string[] | null, national_teams?: string[] | null }

/** The competitions the Home page would list for this account. */
export function visibleCompetitions<T extends LandingCompetition> (list: T[]): T[] {
  return list.filter((c) => !(c.scope === 'national_team' && HIDDEN_COMPETITIONS.includes(c.name)))
}

export const singleCompetitionGuard: CanActivateFn = async (): Promise<boolean | UrlTree> => {
  const auth = inject(AuthService)
  const router = inject(Router)
  const http = inject(HttpClient)

  if (auth.currentUser()?.role !== UserRole.DIRECTOR) return true
  try {
    const [r, reach] = await Promise.all([
      firstValueFrom(http.get<ApiResponse<LandingCompetition[]>>(
        `${environment.apiUrl}/competitions?season=${encodeURIComponent(DEFAULT_SEASON)}`)),
      // A failed reach read counts as nothing granted: the director lands as before.
      firstValueFrom(http.get<ApiResponse<LandingReach>>(`${environment.apiUrlV2}/access/reach`))
        .catch(() => ({ data: {} }) as ApiResponse<LandingReach>),
    ])
    // A director with national teams (Director – national teams) lands where a national-team
    // coach does: his competitions and his national teams as cards, on My teams (role-by-role
    // test, CC 23/10/2026). This page lists competitions only and would leave the sides out.
    if ((reach.data?.national_teams ?? []).length > 0) return router.createUrlTree(['/my-teams'])
    const shown = visibleCompetitions(r.data ?? [])
    return shown.length === 1 ? router.createUrlTree(['/competition', shown[0].id]) : true
  } catch {
    return true
  }
}

export const singleTeamGuard: CanActivateFn = async (): Promise<boolean | UrlTree> => {
  const auth = inject(AuthService)
  const router = inject(Router)
  const http = inject(HttpClient)

  if (!auth.isCoach()) return true
  try {
    const [r, reach] = await Promise.all([
      firstValueFrom(http.get<ApiResponse<LandingTeam[]>>(`${environment.apiUrl}/users/teams`)),
      // A failed reach read counts as nothing granted, so a one-team coach still lands on it.
      firstValueFrom(http.get<ApiResponse<LandingReach>>(`${environment.apiUrlV2}/access/reach`))
        .catch(() => ({ data: {} }) as ApiResponse<LandingReach>),
    ])
    const teams = new Set((r.data ?? []).map((t) => t.id))
    for (const id of reach.data?.national_teams ?? []) teams.add(id)
    const competitions = reach.data?.competitions ?? []
    if (teams.size === 1 && competitions.length === 0) return router.createUrlTree(['/teams', [...teams][0], 'overview'])
    if (teams.size === 0 && competitions.length === 1) return router.createUrlTree(['/competition', competitions[0]])
    return true
  } catch {
    return true
  }
}
